﻿import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, Save } from 'lucide-react';
import {
  ALL_MODULE_CODES,
  ALL_PERMISSION_CODES,
  MODULE_CATEGORY,
  MODULE_LABEL,
  PERMISSION_DESCRIPTION,
  PERMISSION_MODULE_CODE,
  ROLES,
  type ApiError,
  type ModuleCategory,
  type ModuleCode,
  type ModuleInfo,
  type PermissionCode,
} from '@pms/shared';
import { rolesService } from '@/services/roles.service';
import { modulesService } from '@/services/modules.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const CATEGORY_LABEL: Record<ModuleCategory, string> = {
  workspace: 'Workspace',
  productivity: 'Productivity',
  operations: 'Operations',
  finance_resources: 'Finance & Resources',
  teams_support: 'Teams & Support',
  system: 'System',
};

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

export function RoleDetailPage() {
  const { code = '' } = useParams<{ code: string }>();
  const queryClient = useQueryClient();
  const isSuperAdmin = code === ROLES.SUPER_ADMIN;

  const { data: role, isLoading } = useQuery({
    queryKey: ['role', code],
    queryFn: () => rolesService.get(code),
    enabled: !!code,
  });

  const { data: catalog } = useQuery({
    queryKey: ['modules-catalog'],
    queryFn: () => modulesService.list(),
  });

  const [selectedPerms, setSelectedPerms] = useState<Set<PermissionCode>>(new Set());
  const [selectedModules, setSelectedModules] = useState<Set<ModuleCode>>(new Set());
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);

  useEffect(() => {
    if (role) {
      // The DB can hold codes removed from @pms/shared (e.g. office_locations.*);
      // sending them back fails the backend's enum validation with a 400.
      const knownPerms = new Set<string>(ALL_PERMISSION_CODES);
      const knownModules = new Set<string>(ALL_MODULE_CODES);
      setSelectedPerms(new Set(role.permissions.filter((p) => knownPerms.has(p))));
      setSelectedModules(new Set(role.default_modules.filter((m) => knownModules.has(m))));
    }
  }, [role]);

  const permsByModule = useMemo(() => {
    const m = new Map<ModuleCode, PermissionCode[]>();
    for (const p of ALL_PERMISSION_CODES) {
      const mod = PERMISSION_MODULE_CODE[p];
      if (!mod) continue;
      const arr = m.get(mod) ?? [];
      arr.push(p);
      m.set(mod, arr);
    }
    return m;
  }, []);

  const modulesByCategory = useMemo(() => {
    const grouped = new Map<ModuleCategory, ModuleInfo[]>();
    for (const m of catalog ?? []) {
      const arr = grouped.get(m.category) ?? [];
      arr.push(m);
      grouped.set(m.category, arr);
    }
    return grouped;
  }, [catalog]);

  const savePerms = useMutation({
    mutationFn: () =>
      rolesService.setPermissions(code, { permission_codes: Array.from(selectedPerms) }),
    onSuccess: () => {
      toast.success('Permissions saved');
      void queryClient.invalidateQueries({ queryKey: ['role', code] });
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const saveDefaults = useMutation({
    mutationFn: () =>
      rolesService.setDefaultModules(code, { module_codes: Array.from(selectedModules) }),
    onSuccess: () => {
      toast.success('Default modules saved');
      void queryClient.invalidateQueries({ queryKey: ['role', code] });
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const applyToAll = useMutation({
    mutationFn: () => rolesService.applyDefaultsToAll(code),
    onSuccess: (res) => {
      toast.success(`Applied to ${res.affected_count} user${res.affected_count === 1 ? '' : 's'}`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading || !role) {
    return <div className="text-muted-foreground">Loading role…</div>;
  }

  const togglePerm = (p: PermissionCode) => {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const toggleModule = (m: ModuleCode) => {
    setSelectedModules((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });
  };

  const toggleCategory = (cat: ModuleCategory) => {
    const inCat = (modulesByCategory.get(cat) ?? []).map((m) => m.code as ModuleCode);
    setSelectedModules((prev) => {
      const next = new Set(prev);
      const allOn = inCat.every((m) => next.has(m));
      if (allOn) {
        for (const m of inCat) next.delete(m);
      } else {
        for (const m of inCat) next.add(m);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <Link
        to="/system/roles"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to roles
      </Link>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{role.name}</h1>
        <p className="text-sm text-muted-foreground font-mono">{role.code}</p>
        {isSuperAdmin && (
          <p className="mt-2 rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            SUPER_ADMIN bypasses all checks in code. Its permissions and modules are not editable here.
          </p>
        )}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Permissions (Capability)</CardTitle>
          <Button
            size="sm"
            onClick={() => savePerms.mutate()}
            disabled={isSuperAdmin || savePerms.isPending}
          >
            <Save className="h-4 w-4" /> Save Permissions
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from(permsByModule.entries()).map(([modCode, perms]) => (
            <div key={modCode}>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {MODULE_LABEL[modCode] ?? modCode}
              </div>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                {perms.map((p) => (
                  <label
                    key={p}
                    className="flex items-start gap-2 rounded-md border border-border/60 px-3 py-2 text-sm hover:bg-muted/30"
                  >
                    <Checkbox
                      checked={selectedPerms.has(p)}
                      onChange={() => togglePerm(p)}
                      disabled={isSuperAdmin}
                    />
                    <div className="flex-1">
                      <div className="font-mono text-xs">{p}</div>
                      <div className="text-xs text-muted-foreground">
                        {PERMISSION_DESCRIPTION[p]}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Default Modules</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              When a new staff is created with this role, these modules are pre-checked. Use
              &ldquo;Apply to all users&rdquo; to sync existing employees too.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={isSuperAdmin || applyToAll.isPending}
              onClick={() => setApplyConfirmOpen(true)}
            >
              <RefreshCw className="h-4 w-4" />
              Apply to all users
            </Button>
            <Button
              size="sm"
              onClick={() => saveDefaults.mutate()}
              disabled={isSuperAdmin || saveDefaults.isPending}
            >
              <Save className="h-4 w-4" /> Save Defaults
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from(modulesByCategory.entries()).map(([cat, mods]) => (
            <div key={cat}>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {CATEGORY_LABEL[cat]}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs"
                  onClick={() => toggleCategory(cat)}
                  disabled={isSuperAdmin}
                >
                  Toggle all
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                {mods.map((m) => (
                  <label
                    key={m.code}
                    className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2 text-sm hover:bg-muted/30"
                  >
                    <Checkbox
                      checked={selectedModules.has(m.code as ModuleCode)}
                      onChange={() => toggleModule(m.code as ModuleCode)}
                      disabled={isSuperAdmin}
                    />
                    <span className="flex-1 truncate">{m.name}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <AlertDialog open={applyConfirmOpen} onOpenChange={setApplyConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply defaults to all users?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset module access for every employee with the{' '}
              <strong>{role.name}</strong> role to match the current default modules. Any
              individual customisations will be overwritten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setApplyConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setApplyConfirmOpen(false);
                applyToAll.mutate();
              }}
            >
              Yes, apply to all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
// helper export to silence unused warnings if any
export { MODULE_CATEGORY };
